/**
 * The markets and trades V1 serves.
 *
 * Used to be a static file: six call sites had drifted onto different city
 * lists, and adding a market or trade meant a code change + deploy. Now
 * backed by the `trades`/`service_cities` admin-editable tables (issue #331,
 * `apps/backend/src/modules/config/`) -- `useTrades()`/`useServiceCities()`
 * fetch from `GET /api/v1/config/{trades,cities}` and fall back to the
 * fallback list below only until the first fetch resolves (or if it fails),
 * so existing components don't need a loading state to keep working.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiClient } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export interface CityOption {
	value: string;
	label: string;
}

export interface TradeOption {
	value: string;
	labelKey: string;
	displayLabel: string;
}

// i18n keys exist only for the seeded trades (V1's nine and the twelve #362
// added). A trade added later via the admin screen has no translation, so it renders `displayLabel` as-is --
// see the `defaultValue` passed at render sites that call `t(trade.labelKey, ...)`.
const KNOWN_TRADE_LABEL_KEYS: Record<string, string> = {
	electrician: "options.trades.electrician",
	plumber: "options.trades.plumber",
	mason: "options.trades.mason",
	carpenter: "options.trades.carpenter",
	painter: "options.trades.painter",
	general_contractor: "options.trades.generalContractor",
	false_ceiling: "options.trades.falseCeiling",
	flooring: "options.trades.flooring",
	hvac: "options.trades.hvac",
	tiling: "options.trades.tiling",
	roofing: "options.trades.roofing",
	waterproofing: "options.trades.waterproofing",
	kitchen_bath: "options.trades.kitchenBath",
	cabinet_millwork: "options.trades.cabinetMillwork",
	windows_doors: "options.trades.windowsDoors",
	glass: "options.trades.glass",
	welding_metalwork: "options.trades.weldingMetalwork",
	landscaping: "options.trades.landscaping",
	demolition: "options.trades.demolition",
	renovation_remodeling: "options.trades.renovationRemodeling",
	handyman: "options.trades.handyman",
};

// city `value` is the display name itself, not a slug -- existing contractor/
// designer `serviceAreas` jsonb columns already store city names like
// "Delhi", and changing that shape here would silently stop matching them.
const FALLBACK_CITIES: readonly CityOption[] = [
	"Delhi",
	"Gurgaon",
	"Noida",
	"Faridabad",
	"Ghaziabad",
	"Chandigarh",
	"Mohali",
	"Panchkula",
].map((city) => ({ value: city, label: city }));

const FALLBACK_TRADES: readonly TradeOption[] = [
	{ value: "electrician", name: "Electrician" },
	{ value: "plumber", name: "Plumber" },
	{ value: "mason", name: "Mason" },
	{ value: "carpenter", name: "Carpenter" },
	{ value: "painter", name: "Painter" },
	{ value: "general_contractor", name: "General Contractor" },
	{ value: "false_ceiling", name: "False Ceiling" },
	{ value: "flooring", name: "Flooring" },
	{ value: "hvac", name: "HVAC" },
	{ value: "tiling", name: "Tile / Tiling" },
	{ value: "roofing", name: "Roofing" },
	{ value: "waterproofing", name: "Waterproofing" },
	{ value: "kitchen_bath", name: "Kitchen & Bath" },
	{ value: "cabinet_millwork", name: "Cabinet / Millwork" },
	{ value: "windows_doors", name: "Windows & Doors" },
	{ value: "glass", name: "Glass" },
	{ value: "welding_metalwork", name: "Welding / Metalwork" },
	{ value: "landscaping", name: "Landscaping" },
	{ value: "demolition", name: "Demolition" },
	{ value: "renovation_remodeling", name: "Renovation / Remodeling" },
	{ value: "handyman", name: "Handyman" },
].map(({ value, name }) => ({
	value,
	labelKey: KNOWN_TRADE_LABEL_KEYS[value] ?? `options.trades.${value}`,
	displayLabel: name,
}));

interface ConfigCityRow {
	name: string;
	slug: string;
}

interface ConfigTradeRow {
	name: string;
	slug: string;
}

async function fetchServiceCities(): Promise<CityOption[]> {
	// inlineErrors: a failed fetch falls back to the static list below and is
	// invisible to the user -- it should not also toast a scary network error.
	const response = await apiClient.get("/api/v1/config/cities", {
		inlineErrors: true,
	});
	const cities: ConfigCityRow[] = response.data.data.cities;
	return cities.map((city) => ({ value: city.name, label: city.name }));
}

async function fetchTrades(): Promise<TradeOption[]> {
	const response = await apiClient.get("/api/v1/config/trades", {
		inlineErrors: true,
	});
	const trades: ConfigTradeRow[] = response.data.data.trades;
	return trades.map((trade) => ({
		value: trade.slug,
		labelKey:
			KNOWN_TRADE_LABEL_KEYS[trade.slug] ?? `options.trades.${trade.slug}`,
		displayLabel: trade.name,
	}));
}

/** The service cities, as `{value, label}` pairs for a picker where a city must be chosen. */
export function useServiceCities(): readonly CityOption[] {
	const query = useQuery({
		queryKey: queryKeys.config.cities(),
		queryFn: fetchServiceCities,
		staleTime: 5 * 60 * 1000,
	});
	return query.data && query.data.length > 0 ? query.data : FALLBACK_CITIES;
}

/**
 * For filters, where "no city" is a valid choice.
 *
 * The empty `value` is what the browse pages map to their `__all__` sentinel;
 * kept empty here so the filter state stays "unset" rather than a magic string.
 */
export function useCityFilterOptions(): readonly CityOption[] {
	const cities = useServiceCities();
	return useMemo(
		() => [{ value: "", label: "All Cities" }, ...cities],
		[cities],
	);
}

/**
 * The contractor trades, as `{value, labelKey, displayLabel}`.
 *
 * `value` is the slug the API stores and contractor/filter records match
 * against; `displayLabel` is only for rendering.
 */
export function useTrades(): readonly TradeOption[] {
	const query = useQuery({
		queryKey: queryKeys.config.trades(),
		queryFn: fetchTrades,
		staleTime: 5 * 60 * 1000,
	});
	return query.data && query.data.length > 0 ? query.data : FALLBACK_TRADES;
}

/** The seeded trades as a fixed list, for labelling a stored trade slug. */
export const TRADES: readonly TradeOption[] = FALLBACK_TRADES;

/** The trade a stored value names, or undefined for free text a contractor typed. */
export function findTrade(value: string): TradeOption | undefined {
	return TRADES.find((trade) => trade.value === value);
}
